import { create } from "zustand";
import type { PortalProduct } from "../services/portalProductService";

const PORTAL_CART_KEY = "portal_cart";

export interface CartLine {
  product: PortalProduct;
  quantity: number;
  qty_mode?: "units" | "packs";
  selected_pack_size?: number | null;
}

/** Serializable shape for localStorage (Story 7-4). */
interface StoredCartLine {
  productId: string;
  quantity: number;
  product: PortalProduct;
  qty_mode?: "units" | "packs";
  selected_pack_size?: number | null;
}

function persist(lines: CartLine[]): void {
  try {
    const stored: StoredCartLine[] = lines.map((l) => ({
      productId: l.product.id,
      quantity: l.quantity,
      product: l.product,
      qty_mode: l.qty_mode ?? "units",
      selected_pack_size: l.selected_pack_size ?? null,
    }));
    localStorage.setItem(PORTAL_CART_KEY, JSON.stringify(stored));
  } catch {
    // ignore quota or parse errors
  }
}

function loadFromStorage(): CartLine[] {
  try {
    const raw = localStorage.getItem(PORTAL_CART_KEY);
    if (!raw) return [];
    const parsed: StoredCartLine[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item?.productId && item?.quantity > 0 && item?.product?.id)
      .map((item) => ({
        product: item.product as PortalProduct,
        quantity: Math.max(1, Number(item.quantity) || 1),
        qty_mode: item.qty_mode === "packs" ? "packs" : "units",
        selected_pack_size:
          typeof item.selected_pack_size === "number" && item.selected_pack_size > 0
            ? item.selected_pack_size
            : item.product?.sales_pack_sizes?.[0] ?? null,
      }));
  } catch {
    return [];
  }
}

interface PortalCartState {
  lines: CartLine[];
  _hydrated: boolean;
  addItem: (product: PortalProduct, quantity: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  setLineQtyMode: (productId: string, mode: "units" | "packs") => void;
  setLinePackSize: (productId: string, packSize: number) => void;
  clearCart: () => void;
  itemCount: () => number;
  hydrateFromStorage: () => void;
}

export const usePortalCartStore = create<PortalCartState>((set, get) => ({
  lines: [],
  _hydrated: false,

  addItem: (product, quantity) => {
    set((state) => {
      const existing = state.lines.find((l) => l.product.id === product.id);
      const newLines = existing
        ? state.lines.map((l) =>
            l.product.id === product.id ? { ...l, quantity: l.quantity + quantity } : l
          )
        : [
            ...state.lines,
            {
              product,
              quantity,
              qty_mode: "units" as const,
              selected_pack_size: product.sales_pack_sizes?.[0] ?? null,
            },
          ];
      persist(newLines);
      return { lines: newLines };
    });
  },

  removeItem: (productId) => {
    set((state) => {
      const newLines = state.lines.filter((l) => l.product.id !== productId);
      persist(newLines);
      return { lines: newLines };
    });
  },

  updateQuantity: (productId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(productId);
      return;
    }
    set((state) => {
      const newLines = state.lines.map((l) =>
        l.product.id === productId ? { ...l, quantity } : l
      );
      persist(newLines);
      return { lines: newLines };
    });
  },

  setLineQtyMode: (productId, mode) => {
    set((state) => {
      const newLines = state.lines.map((l) => {
        if (l.product.id !== productId) return l;
        if (mode !== "packs") return { ...l, qty_mode: "units" as const };

        const size = l.selected_pack_size ?? l.product.sales_pack_sizes?.[0] ?? 1;
        if (size <= 1) return { ...l, qty_mode: "units" as const, selected_pack_size: size };
        const packs = Math.max(1, Math.floor(l.quantity / size));
        return {
          ...l,
          qty_mode: "packs" as const,
          selected_pack_size: size,
          quantity: packs * size,
        };
      });
      persist(newLines);
      return { lines: newLines };
    });
  },

  setLinePackSize: (productId, packSize) => {
    if (!Number.isFinite(packSize) || packSize <= 0) return;
    set((state) => {
      const newLines = state.lines.map((l) => {
        if (l.product.id !== productId) return l;
        if ((l.qty_mode ?? "units") !== "packs") {
          return { ...l, selected_pack_size: packSize };
        }
        const previousSize = l.selected_pack_size ?? l.product.sales_pack_sizes?.[0] ?? packSize;
        const currentPacks = Math.max(1, Math.floor(l.quantity / Math.max(1, previousSize)));
        return {
          ...l,
          selected_pack_size: packSize,
          quantity: currentPacks * packSize,
        };
      });
      persist(newLines);
      return { lines: newLines };
    });
  },

  clearCart: () => {
    persist([]);
    set({ lines: [] });
  },

  itemCount: () => get().lines.reduce((sum, l) => sum + l.quantity, 0),

  hydrateFromStorage: () => {
    if (get()._hydrated) return;
    const lines = loadFromStorage();
    set({ lines, _hydrated: true });
  },
}));
